// For more info see https://webrtc.org/
// This file is supposed to track 1-1 backend/node/captioner/api/helpers/webrtc.ts
// In ideal scenario the WebRTCManager on all the sides of the WebRTC communication
// has the same exact code, and the only asymmetry should be 'politeness'.

import { singletonTrackPlayer } from './audio';
import { log } from './log';

const WEBRTC_CONFIG = {
  // Publicly available Google STUN server. No SLAs, but it's Google. Eventually
  // we should have a few other STUN servers here, or maybe host our own.
  iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
  // Defaults to 0, but this might make establishing a connection faster
  // iceCandidatePoolSize: 10,
};

export class WebRTCManager {
  peerConnection = undefined; // RTCPeerConnection
  onMessage = undefined;
  messageBuffer = [];
  trackListeners = [];
  // https://developer.mozilla.org/en-US/docs/Web/API/WebRTC_API/Perfect_negotiation
  // Perfect negotiation involves a 'polite' and 'impolite' peers. That way
  // it's easy to handle offer collisions - polite peers always drop their own
  // offers, while impolite always force their own offers. Otherwise figuring
  // out which offer to pursue while both are in transit would be difficult.
  polite = false;
  makingOffer = false;

  info(message) {
    log('webrtc', 'info', message);
  }

  error(message) {
    log('webrtc', 'error', message);
  }

  constructor(polite) {
    // The object can be constructed early, and it will be able to handle
    // messages early. However, it will buffer all the signaling messages
    // it wants to send, and only send them when initialize is called.
    this.polite = polite;
    this.info(`constructing WebRTCManager with politeness ${polite}`);

    this.peerConnection = new RTCPeerConnection(WEBRTC_CONFIG);
    this.peerConnection.addEventListener('icecandidate', event => {
      this.info('ice candidate appeared');
      if (event.candidate) {
        this.handleLocalIceCandidate(event.candidate);
      }
    });
    this.peerConnection.addEventListener('connectionstatechange', event => {
      this.info(`connection state changed ${this.peerConnection.connectionState}`);
    });
    this.peerConnection.addEventListener('signalingstatechange', event => {
      this.info(`signalingstatechange ${this.peerConnection.signalingState}`);
    });
    this.peerConnection.addEventListener('negotiationneeded', async event => {
      this.info('negotiation is needed');
      this.initiateNegotiation();
    });
    this.peerConnection.addEventListener('track', event => {
      this.info(`remote track appeared ${event.track.id}`);
      this.trackListeners.forEach(f => f(event.track));
    });
  }

  destructor() {
    this.peerConnection.close();
  }

  initialize(onMessage) {
    // This can only be called once the signaling channel (onMessage function) is ready
    // to accept messages. It will flush all the messages accumulated in the buffer
    // before the signaling channel was ready.
    this.onMessage = onMessage;
    this.maybeFlushMessageBuffer();
  }

  addTrackListener(f) {
    // This will get triggered any time the server publishes a new track.
    this.trackListeners.push(f);
  }

  async initiateNegotiation() {
    this.info('initiating offer');
    try {
      this.makingOffer = true;
      // Calling it with no arguments 'does the right thing'
      // (check the 'perfect negotiation' link above).
      await this.peerConnection.setLocalDescription();
      await this.sendMessage({
        offer: this.peerConnection.localDescription,
      });
    } finally {
      this.makingOffer = false;
    }
  }

  async handleV1Message(message) {
    if (!message) return false;
    if (message.offer && message.answer) {
      this.error('message cannot contain both offer and answer');
      return false;
    }
    if (message.offer) {
      this.info('handling webrtc offer');
      await this.handleOffer(message.offer);
      return true;
    }
    if (message.answer) {
      this.info('accepting webrtc answer');
      await this.acceptAnswer(message.answer);
      return true;
    }
    if (message.iceCandidate) {
      this.info('received remote ICE candidate');
      await this.addRemoteIceCandidate(message.iceCandidate);
      return true;
    }
    return false;
  }

  async handleOffer(offer) {
    const offerCollision = this.makingOffer || this.peerConnection.signalingState !== 'stable';
    if (offerCollision) {
      if (!this.polite) {
        this.info('offer being ignored, we have our own');
        return;
      }
      if (this.polite) {
        await this.peerConnection.setLocalDescription({ type: 'rollback' });
      }
    }
    await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);
    this.sendMessage({
      answer,
    });
  }

  async acceptAnswer(answer) {
    const remoteDesc = new RTCSessionDescription(answer);
    await this.peerConnection.setRemoteDescription(remoteDesc);
  }

  async addRemoteIceCandidate(iceCandidate) {
    try {
      await this.peerConnection.addIceCandidate(iceCandidate);
    } catch (e) {
      this.info(e);
    }
  }

  handleLocalIceCandidate(candidate) {
    this.sendMessage({
      iceCandidate: candidate,
    });
  }

  sendMessage(message) {
    this.messageBuffer.push(message);
    this.maybeFlushMessageBuffer();
  }
  maybeFlushMessageBuffer() {
    if (this.onMessage) {
      for (const message of this.messageBuffer) {
        this.onMessage(message);
      }
      this.messageBuffer = [];
    }
  }

  addTrack(track) {
    // Optionally, a stream can be added here too.
    return this.peerConnection.addTrack(track);
  }
}

let singletonWebRTCManager;

export const recreateSingletonWebRTCManager = () => {
  if (singletonWebRTCManager) singletonWebRTCManager.destructor();
  singletonWebRTCManager = new WebRTCManager(true);
  // Currently we play the most recent track added by the server. This can
  // ultimately be smarter, and we can potentially hold a few tracks and choose
  // them on the client rather than by sending a new downAudio request.
  singletonWebRTCManager.addTrackListener(track => singletonTrackPlayer.setTrack(track));
};

export const getSingletonWebRTCManager = () => {
  if (!singletonWebRTCManager) recreateSingletonWebRTCManager();
  return singletonWebRTCManager;
};
